/**
 * Phase 6 cross-cutting PR 4 — proactive trigger detectors.
 *
 * Five pure-function detectors that take observable athlete state and
 * return either a fired `ProactiveTriggerEvent` or `null`. The hook
 * `useProactiveChecks` orchestrates them — these functions know
 * nothing about React, hooks, network, or persistence.
 *
 * Detectors are ports of vanilla `proactive-checks.js` (158 lines),
 * preserving every threshold + condition.
 *
 * Per-day idempotency is the CALLER'S responsibility (via the
 * `_proactive_last_<type>` debounce keys). These detectors fire
 * whenever their conditions match — they don't check "already fired
 * today".
 */

import {
  comebackCopy,
  complianceDropCopy,
  raceCountdownCopy,
  streakMilestoneCopy,
  prCelebrationCopy,
  draftNextWeekCopy,
  unloggedSessionsCopy,
  seasonWrapCopy,
  STREAK_MILESTONES,
  type PlanLifecycleTriggerType,
  type ProactiveCopy,
  type ProactiveTriggerType,
} from './proactiveCopy';
import { todayMondayBasedLocal } from './weekdayUtils';

export interface ProactiveTriggerEvent extends ProactiveCopy {
  type: ProactiveTriggerType;
}

// --- Time helpers ---------------------------------------------------------

/** Local-timezone YYYY-MM-DD (matches vanilla `today()` impl). */
export function todayLocalIsoDate(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Whole-day delta between today and `dateStr` (YYYY-MM-DD).
 * Returns `Infinity` if `dateStr` is missing/invalid.
 */
export function daysSince(dateStr: string | null | undefined, now: Date = new Date()): number {
  if (!dateStr) return Infinity;
  const target = new Date(`${dateStr}T00:00:00`).getTime();
  if (isNaN(target)) return Infinity;
  return Math.floor((now.getTime() - target) / 86400000);
}

// --- Detector inputs ------------------------------------------------------

/**
 * Minimal shape each detector needs. Caller (the hook) builds this
 * from the various data hooks (useAthleteContext, useWeeklyPlan,
 * useMacroPlan, useUpcomingRace, useRaceResults, useTeamMeets, etc.)
 * and passes it in. Keeping detectors decoupled from the data hooks
 * makes them trivially unit-testable.
 */
export interface ProactiveDetectorInput {
  /** Last time the athlete loaded /v2/today. ISO YYYY-MM-DD or null. */
  lastDashboardVisit: string | null;
  /** This week's compliance % (0-100). Null if no plan exists. */
  thisWeekCompliancePct: number | null;
  /** Last week's compliance % (0-100). Null if no prior plan. */
  lastWeekCompliancePct: number | null;
  /** Upcoming meets in chronological order (earliest first). */
  upcomingMeets: ReadonlyArray<{ meet_name: string; meet_date: string }>;
  /** Current consecutive-completed training-day streak. */
  currentStreak: number;
  /** Race results in reverse-chronological order (most recent first). */
  raceResults: ReadonlyArray<{ is_pr: boolean; race_date: string; distance: string; finish_time: string }>;
  /**
   * NU-2 — plan-lifecycle state for the nudge trio. Optional: the 5
   * legacy detectors ignore it, and `null`/absent (flag off, or the
   * caller's data hooks not settled) disables all three lifecycle
   * detectors.
   */
  planLifecycle?: PlanLifecycleInput | null;
}

/** NU-2 — input for the plan-lifecycle nudge detectors. */
export interface PlanLifecycleInput {
  /** `resolveCurrentWeek(macroPlan, null)` state. */
  seasonState: 'before_season' | 'in_season' | 'after_season' | 'no_plan' | 'invalid' | null;
  /** Current macro week (null outside in_season). */
  currentWeekNumber: number | null;
  /** Macro plan length (0 = no plan). */
  totalWeeks: number;
  /** Weeks with a drafted training_plans row (draftedWeeksFromPlans). */
  draftedWeeks: ReadonlySet<number>;
  /**
   * The CURRENT week's positional Mon..Sun day cells (useWeeklyPlan
   * always emits 7, padding plan-less weekdays as rest), or null when
   * no week plan exists.
   */
  weekDays: ReadonlyArray<{
    rest?: boolean;
    completionStatus?: 'completed' | 'skipped' | 'upcoming' | null;
  }> | null;
  /**
   * Stored `_proactive_last_<type>` values for the trio. Lets the
   * `detectPlanNudge` cap pick the highest-priority nudge that has NOT
   * already fired — without this, a debounced season_wrap would win the
   * cap every day of the final week and starve the other two (the
   * orchestrator would drop it post-cap, firing nothing).
   */
  lastFired?: Partial<Record<PlanLifecycleTriggerType, string | undefined>>;
}

// --- Five detectors (ports of proactive-checks.js) -----------------------

/**
 * 1. comeback (proactive-checks.js:56-65)
 * Condition: lastDashboardVisit ≥ 3 days ago AND < 365 days ago.
 * Skips brand-new users (no lastDashboardVisit set).
 *
 * ⚖️ B7 GROUNDING (2026-08-22) — the trigger's datum is DASHBOARD absence,
 * but the copy ("It's been N days") reads as TRAINING absence, and the
 * motivation LLM weaves the claim further ("25 days off is like a spoiler",
 * rendered to an ACTIVE athlete — the founder-reported B7). `_last_dashboard_
 * visit` is written only by /v2/today: an athlete living in the run screen
 * and chat — or whose debounced context write silently failed — accrues
 * phantom "days away" while training daily. The claim must therefore be
 * corroborated before it fires: any positive training evidence the detector
 * input ALREADY carries (a live completed-day streak, completed sessions this
 * week, a race inside the absence window) refutes "you've been away" and
 * suppresses the trigger. Fail to NO teaser, never to a false claim. The
 * genuinely-away athlete (no streak, no completions, no recent race) fires
 * exactly as vanilla did.
 */
export function detectComeback(
  input: ProactiveDetectorInput,
  now: Date = new Date(),
): ProactiveTriggerEvent | null {
  const days = daysSince(input.lastDashboardVisit, now);
  if (input.lastDashboardVisit && days >= 3 && days < 365) {
    // B7 — training evidence refutes the absence claim; say nothing.
    if (input.currentStreak > 0) return null;
    if ((input.thisWeekCompliancePct ?? 0) > 0) return null;
    if (input.raceResults.some((r) => daysSince(r.race_date, now) <= days)) return null;
    return { type: 'comeback', ...comebackCopy(days) };
  }
  return null;
}

/**
 * 2. compliance_drop (proactive-checks.js:69-93)
 * Condition: this week < 50% AND last week > 70%.
 * Requires both weeks to have data.
 */
export function detectComplianceDrop(
  input: ProactiveDetectorInput,
): ProactiveTriggerEvent | null {
  const { thisWeekCompliancePct: thisWeek, lastWeekCompliancePct: lastWeek } = input;
  if (thisWeek !== null && lastWeek !== null && thisWeek < 50 && lastWeek > 70) {
    return { type: 'compliance_drop', ...complianceDropCopy() };
  }
  return null;
}

/**
 * 3. race_countdown (proactive-checks.js:97-114)
 * Condition: any upcoming meet within 0-3 days. First matching meet wins.
 * **D2 reroute applied via copy module (notificationType = motivational_message).**
 */
export function detectRaceCountdown(
  input: ProactiveDetectorInput,
  now: Date = new Date(),
): ProactiveTriggerEvent | null {
  for (const meet of input.upcomingMeets) {
    const daysUntil = Math.ceil(
      (new Date(`${meet.meet_date}T00:00:00`).getTime() - now.getTime()) / 86400000,
    );
    if (daysUntil >= 0 && daysUntil <= 3) {
      return {
        type: 'race_countdown',
        ...raceCountdownCopy(daysUntil),
      };
    }
  }
  return null;
}

/**
 * 4. streak_milestone (proactive-checks.js:117-132)
 * Condition: currentStreak ∈ {7, 14, 21, 30, 50, 100}.
 * Caller computes the streak from plan_days (consecutive completed
 * non-rest days).
 */
export function detectStreakMilestone(
  input: ProactiveDetectorInput,
): ProactiveTriggerEvent | null {
  if (STREAK_MILESTONES.includes(input.currentStreak)) {
    return { type: 'streak_milestone', ...streakMilestoneCopy(input.currentStreak) };
  }
  return null;
}

/**
 * 5. pr_celebration (proactive-checks.js:135-146)
 * Condition: a race with is_pr=true in the last ≤ 2 days. First
 * matching race wins.
 */
export function detectPrCelebration(
  input: ProactiveDetectorInput,
  now: Date = new Date(),
): ProactiveTriggerEvent | null {
  for (const race of input.raceResults) {
    if (race.is_pr && daysSince(race.race_date, now) <= 2) {
      return {
        type: 'pr_celebration',
        ...prCelebrationCopy(race.distance, race.finish_time),
      };
    }
  }
  return null;
}

// --- NU-2 — plan-lifecycle nudge detectors (flag `plan_nudges`) -----------
//
// New deterministic detectors (not vanilla ports). All three gate on
// `input.planLifecycle` being present — absent/null (flag off or data
// hooks unsettled) means none fire. `detectPlanNudge` caps the trio to
// at most ONE event per run (D2).

/**
 * 6. draft_next_week — from Thursday (Monday-based dow ≥ 3), in season,
 * when next week exists in the macro plan and isn't drafted yet. The
 * copy's `debounceValue` is the TARGET WEEK, so it fires once per week
 * to draft (not daily Thu–Sun).
 */
export function detectDraftNextWeek(
  input: ProactiveDetectorInput,
  now: Date = new Date(),
): ProactiveTriggerEvent | null {
  const pl = input.planLifecycle;
  if (!pl) return null;
  if (pl.seasonState !== 'in_season' || pl.currentWeekNumber === null) return null;
  const next = pl.currentWeekNumber + 1;
  if (next > pl.totalWeeks) return null; // final week → season_wrap territory
  if (pl.draftedWeeks.has(next)) return null;
  if (todayMondayBasedLocal(now) < 3) return null; // Mon-Wed: too early to nag
  return { type: 'draft_next_week', ...draftNextWeekCopy(next) };
}

/**
 * 7. unlogged_sessions — ≥ 2 planned (non-rest) days strictly BEFORE
 * today in the current week with completionStatus !== 'completed'
 * ('skipped' counts — it still wears the dimmed/uncredited treatment).
 * Monday never fires (no prior days). Daily debounce (no debounceValue).
 */
export function detectUnloggedSessions(
  input: ProactiveDetectorInput,
  now: Date = new Date(),
): ProactiveTriggerEvent | null {
  const pl = input.planLifecycle;
  if (!pl || !pl.weekDays) return null;
  const todayIdx = todayMondayBasedLocal(now);
  let count = 0;
  for (let dow = 0; dow < Math.min(todayIdx, pl.weekDays.length); dow++) {
    const day = pl.weekDays[dow];
    if (!day || day.rest) continue;
    if (day.completionStatus !== 'completed') count++;
  }
  if (count < 2) return null;
  return { type: 'unlogged_sessions', ...unloggedSessionsCopy(count) };
}

/**
 * 8. season_wrap — the season's final in-season week ('final_week') or
 * any visit after the season ended ('after_season'). Each variant fires
 * exactly once (debounceValue = variant; distinct titles defeat the
 * server's 20h title dedup across the transition).
 */
export function detectSeasonWrap(input: ProactiveDetectorInput): ProactiveTriggerEvent | null {
  const pl = input.planLifecycle;
  if (!pl) return null;
  if (pl.seasonState === 'after_season') {
    return { type: 'season_wrap', ...seasonWrapCopy('after_season') };
  }
  if (
    pl.seasonState === 'in_season' &&
    pl.currentWeekNumber !== null &&
    pl.totalWeeks > 0 &&
    pl.currentWeekNumber === pl.totalWeeks
  ) {
    return { type: 'season_wrap', ...seasonWrapCopy('final_week') };
  }
  return null;
}

/**
 * NU-2 (D2) — the lifecycle cap: at most ONE nudge per run, priority
 * season_wrap > draft_next_week > unlogged_sessions. Candidates whose
 * `lastFired` value already equals their debounceValue are passed over
 * (already nudged) so the next-priority FRESH nudge surfaces instead of
 * nothing. A suppressed candidate's debounce key is never written — it
 * gets its turn on a later day.
 */
export function detectPlanNudge(
  input: ProactiveDetectorInput,
  now: Date = new Date(),
): ProactiveTriggerEvent | null {
  const pl = input.planLifecycle;
  if (!pl) return null;
  const candidates = [
    detectSeasonWrap(input),
    detectDraftNextWeek(input, now),
    detectUnloggedSessions(input, now),
  ].filter((e): e is ProactiveTriggerEvent => e !== null);
  const today = todayLocalIsoDate(now);
  const lastFired = pl.lastFired ?? {};
  for (const candidate of candidates) {
    const stored = lastFired[candidate.type as PlanLifecycleTriggerType];
    if (stored !== undefined && stored === (candidate.debounceValue ?? today)) continue;
    return candidate;
  }
  return null;
}

/**
 * Run all detectors and return every triggered event (the lifecycle
 * trio contributes at most ONE via `detectPlanNudge`). Order is NOT
 * priority order — the caller (or the consumer of this array) applies
 * `PROACTIVE_PRIORITY` to pick the primary hint.
 */
export function detectAllTriggers(
  input: ProactiveDetectorInput,
  now: Date = new Date(),
): ProactiveTriggerEvent[] {
  return [
    detectComeback(input, now),
    detectComplianceDrop(input),
    detectRaceCountdown(input, now),
    detectStreakMilestone(input),
    detectPrCelebration(input, now),
    detectPlanNudge(input, now),
  ].filter((e): e is ProactiveTriggerEvent => e !== null);
}
